import type {VolatilePattern} from './LFCPgHistoryAccumUtil';

export type CompiledVolatileRule = {regex: RegExp; replacement: string; drop: boolean};

/**
 * Python の `re.sub` の置換文字列を JavaScript の `String.replace` の書き方へ変換する。
 * `\g<0>` は全体一致（`$&`）。`\0` / `\0NN`（Python では NUL などの 8 進エスケープ）は JS に同等の書き方がないため例外とする。
 */
export const toJsReplacement = (python: string): string =>
  python.replace(/\\(?:g<(\d+|[A-Za-z_]\w*)>|(\d+)|(\\))|\$/g, (match, ref, number, backslash) => {
    if (match === '$') return '$$';
    if (backslash) return '\\';
    const target = ref ?? number;
    if (target === '0' && ref !== undefined) return '$&';
    if (ref === undefined && target.startsWith('0')) {
      throw new Error('JavaScript に変換できない置換文字列です（8 進エスケープ）');
    }
    return /^\d+$/.test(target) ? `$${target}` : `$<${target}>`;
  });

/**
 * プロファイルの揮発値ルール（Python の正規表現）を JavaScript でコンパイルする。
 * 方言の違いで 1 本でも解釈できなければ、ルールの一部だけを当てた比較にならないよう null を返す。
 */
export const compileVolatileRules = (
  patterns: readonly VolatilePattern[]
): CompiledVolatileRule[] | null => {
  try {
    return patterns.map(p => ({
      regex: new RegExp(p.pattern, p.drop === true ? 'u' : 'gu'),
      replacement: toJsReplacement(p.replacement),
      drop: p.drop === true
    }));
  } catch {
    return null;
  }
};

/** ゲートウェイの `git_change_sha` と同じ順序でルールを当てる。drop に一致した行は null */
export const applyVolatileRules = (
  lines: readonly string[],
  rules: readonly CompiledVolatileRule[]
): (string | null)[] =>
  lines.map(original => {
    // ゲートウェイは CRLF / CR を LF に統一してから当てるため、UI 側の行末の CR は取り除く（比較専用）
    let line = original.replace(/\r$/, '');
    for (const rule of rules) {
      if (rule.drop) {
        if (rule.regex.test(line)) return null;
        continue;
      }
      line = line.replace(rule.regex, rule.replacement);
    }
    return line;
  });
